import * as dates from "@client/dates";
import * as dialogs from "@client/features/dialogs";
import {notify} from "@client/features/notifications";
import * as orm from "@client/features/orm";
import * as hardware from "@client/hardware";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import {store, useAppDispatch, useAppSelector} from "@client/store";
import * as utils from "@client/utils";
import {server} from "@reactivated";
import * as Sentry from "@sentry/browser";
import {Decimal} from "decimal.js";
import {produce} from "immer";
import React from "react";

async function requestAuthorization() {
    void analytics.track("REQUESTED_APPLE_HEALTH_AUTHORIZATION");

    try {
        await hardware.HealthPlugin.requestAuthorization(
            hardware.HEALTHKIT_PERMISSIONS,
        );
    } catch (error) {
        Sentry.captureException(error);
        console.error("[HealthKitService] Error getting Authorization:", error);
    }
}

interface NightAggregate {
    date: string;
    total_duration: number; // in seconds (merged, deduplicated)
    entry_count: number;
    start_time: string;
    end_time: string;
    human: string;
    apple_health_uuids: UUID[];
    sources: string[];
}

function isAsleepState(state: hardware.SleepState): boolean {
    return (
        state === "Asleep" ||
        state === "AsleepREM" ||
        state === "AsleepCore" ||
        state === "AsleepDeep" ||
        state === "AsleepUnspecified"
    );
}

function mergeIntervals(
    intervals: Array<{start: number; end: number}>,
): Array<{start: number; end: number}> {
    if (intervals.length === 0) return [];

    const sorted = [...intervals].sort((a, b) => a.start - b.start);
    const merged: Array<{start: number; end: number}> = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
        const current = sorted[i];
        const last = merged[merged.length - 1];

        if (current.start <= last.end) {
            last.end = Math.max(last.end, current.end);
        } else {
            merged.push({...current});
        }
    }

    return merged;
}

export function aggregateWorkoutsByDate(
    workouts: hardware.HealthKitWorkout[],
): Record<DateValue, server.journal.day.WorkoutAggregate.input[]> {
    const result: Record<string, server.journal.day.WorkoutAggregate.input[]> = {};
    const deviceTimezone = dates.deviceTimezone();

    // Deduplicate workouts before processing
    // Group by: startDate, endDate, duration, sourceBundleId
    const workoutGroups = new Map<string, hardware.HealthKitWorkout[]>();

    for (const workout of workouts) {
        const key = `${workout.startDate}|${workout.endDate}|${Math.round(workout.duration)}|${workout.sourceBundleId}`;

        if (!workoutGroups.has(key)) {
            workoutGroups.set(key, []);
        }
        workoutGroups.get(key)!.push(workout);
    }

    // Keep one workout from each group (prefer one with distance if available)
    const deduplicatedWorkouts = Array.from(workoutGroups.values()).map((group) => {
        // Prefer workout with distance_meters if available
        const withDistance = group.find((w) => w.totalDistance != null);
        return withDistance ?? group[0];
    });

    for (const workout of deduplicatedWorkouts) {
        const date = dates.zonedDate(
            new Date(workout.startDate),
            workout.timezone ?? deviceTimezone,
        );

        // Initialize array if needed
        if (!(date in result)) {
            result[date] = [];
        }

        // Create the aggregate input
        result[date].push({
            apple_health_uuid: workout.uuid as UUID,
            workout_activity_type: workout.workoutActivityType,
            workout_activity_id: workout.workoutActivityId,
            start_time: workout.startDate,
            end_time: workout.endDate,
            timezone: workout.timezone ?? deviceTimezone,
            duration_seconds: Math.round(workout.duration),
            calories: workout.totalEnergyBurned?.toString() ?? null,
            distance_meters: workout.totalDistance?.toString() ?? null,
            source: workout.source,
            source_bundle_id: workout.sourceBundleId,
        });
    }

    return result;
}

export function aggregateSleepByNight(
    sleepEntries: hardware.HealthKitSleep[],
    cutoffHour: number = 15,
): NightAggregate[] {
    // Filter for asleep entries only (ignore InBed and Awake)
    const asleepEntries = sleepEntries.filter((entry) =>
        isAsleepState(entry.sleepState),
    );

    // Group entries by night
    const nightsMap: Map<string, hardware.HealthKitSleep[]> = new Map();

    for (const entry of asleepEntries) {
        let entryDate = dates.parseISO(entry.startDate);

        // If the entry is before the cutoff, it belongs to the previous day's night
        if (entryDate.getHours() < cutoffHour) {
            entryDate = dates.addDays(entryDate, -1);
        }

        const nightKey = dates.format(entryDate, "yyyy-MM-dd");

        if (!nightsMap.has(nightKey)) {
            nightsMap.set(nightKey, []);
        }
        nightsMap.get(nightKey)!.push(entry);
    }

    // Aggregate data for each night using interval merging
    const nightAggregates: NightAggregate[] = [];

    Array.from(nightsMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([, entries]) => {
            // Convert entries to timestamp intervals and merge overlapping ones
            const intervals = entries.map((entry) => ({
                start: dates.parseISO(entry.startDate).getTime(),
                end: dates.parseISO(entry.endDate).getTime(),
            }));

            const merged = mergeIntervals(intervals);

            const totalDuration = merged.reduce(
                (sum, interval) =>
                    sum + Math.round((interval.end - interval.start) / 1000),
                0,
            );

            const startTime = dates.min(
                entries.map((e) => dates.parseISO(e.startDate)),
            );
            const endTime = dates.max(entries.map((e) => dates.parseISO(e.endDate)));

            const sources = [...new Set(entries.map((e) => e.source))];

            nightAggregates.push({
                date: dates.format(endTime, "yyyy-MM-dd"),
                total_duration: totalDuration,
                entry_count: entries.length,
                start_time: startTime.toISOString(),
                end_time: endTime.toISOString(),
                apple_health_uuids: entries.map((e) => e.uuid),
                sources,
                human: utils.convertSecondsToHoursMinutes(totalDuration),
            });
        });

    return nightAggregates;
}

export async function checkPermissions() {
    const allPermissions = [
        ...hardware.HEALTHKIT_PERMISSIONS.all,
        ...hardware.HEALTHKIT_PERMISSIONS.read,
        ...hardware.HEALTHKIT_PERMISSIONS.write,
    ];

    return (
        await Promise.all(
            allPermissions.map(async (sampleType) => {
                const result = await hardware.HealthPlugin.authorizationStatus({
                    sampleType,
                }).catch(() => undefined);
                if (result == null) {
                    return undefined;
                }

                return {sampleType, status: result.status};
            }),
        )
    ).filter((status) => status != null);
}

export async function syncHealthData(
    profile: server.core.rpc.Profile.output,
    days: number,
    trace: string,
    // How many days back the window *ends*. 0 = ends today (the normal case).
    // Pass a non-zero delta to sync an older window, e.g. for a staggered resync.
    offsetDays: number = 0,
): Promise<boolean> {
    const endDate = dates.format(dates.subDays(new Date(), offsetDays), "yyyy-MM-dd");
    const startDate = dates.format(
        dates.subDays(new Date(), offsetDays + days),
        "yyyy-MM-dd",
    );
    // The first day of a statistics query reads low when another source
    // (Watch, WHOOP) wrote samples that start before the window, so every
    // aggregate query starts a day early and drops that day (docs/healthkit.md).
    const paddedStart = dates.format(
        dates.subDays(new Date(), offsetDays + days + 1),
        "yyyy-MM-dd",
    );
    const queryAggregate = async (
        sample: "stepCount" | "basalEnergyBurned" | "activeEnergyBurned",
    ) => {
        const {results} = await hardware.HealthPlugin.queryAggregate({
            sample,
            startDate: paddedStart,
            endDate,
        });
        return {results: results.filter((aggregate) => aggregate.date >= startDate)};
    };

    const stepsAggregates = await queryAggregate("stepCount");
    const basalAggregates = await queryAggregate("basalEnergyBurned");
    const activeAggregates = await queryAggregate("activeEnergyBurned");

    const queryOptions = {
        startDate,
        endDate,
        limit: 0,
    };

    const samples = {
        WORKOUT: await hardware.HealthPlugin.querySample({
            ...queryOptions,
            sample: "workoutType",
        }).catch(() => undefined),
        WEIGHT: await hardware.HealthPlugin.querySample({
            ...queryOptions,
            sample: "bodyMass",
        }),
        SLEEP: await hardware.HealthPlugin.querySample({
            ...queryOptions,
            sample: "sleepAnalysis",
        }),
    };

    const deviceTimezone = dates.deviceTimezone();

    // Today's per-hour step buckets for the walking club pulse. An old binary
    // ignores the interval option and answers with one daily bucket, so the
    // sub-day entry shape is the capability signal — record_pulse's estimate
    // stays the fallback and nothing depends on the binary version.
    const todayKey = dates.format(new Date(), "yyyy-MM-dd");
    const hourlyAggregates =
        offsetDays === 0
            ? await hardware.HealthPlugin.queryAggregate({
                  sample: "stepCount",
                  startDate: dates.format(dates.subDays(new Date(), 1), "yyyy-MM-dd"),
                  endDate: todayKey,
                  interval: "hour",
              })
            : null;
    const hourlyResults = (hourlyAggregates?.results ?? []).filter(
        (entry) =>
            entry.date === todayKey &&
            dates.parseISO(entry.endDate).getTime() -
                dates.parseISO(entry.startDate).getTime() <=
                60 * 60 * 1000,
    );
    const hourlySteps =
        hourlyResults.length > 0
            ? {
                  date: todayKey,
                  timezone: deviceTimezone,
                  buckets: Array.from({length: 24}, (_, hour) =>
                      Math.round(
                          hourlyResults
                              .filter(
                                  (entry) =>
                                      dates.parseISO(entry.startDate).getHours() ===
                                      hour,
                              )
                              .reduce((sum, entry) => sum + entry.value, 0),
                      ),
                  ),
              }
            : null;

    const prepareMetric = (
        type: server.journal.day.Metric.input["type"],
        results: (
            | hardware.HealthKitUnknown
            | hardware.HealthKitSleep
            | hardware.HealthKitWorkout
        )[],
    ): server.journal.day.Metric.input[] => {
        return results.map((result) => ({
            type,
            uuid: crypto.randomUUID(),
            apple_health_uuid: result.uuid as UUID,
            apple_health_data: JSON.stringify(result),
            value: new Decimal(
                ("value" in result ? result.value : result.duration).toString(),
            ).toString(),
            start_date: result.startDate,
            end_date: result.endDate,
            timezone: result.timezone ?? deviceTimezone,
        }));
    };
    const workouts = aggregateWorkoutsByDate(samples.WORKOUT?.resultData ?? []);

    const metrics: Metrics = {};

    const sleep = aggregateSleepByNight(samples.SLEEP.resultData);

    const weight = samples.WEIGHT.resultData.reduce<
        Record<string, server.journal.day.MetricAggregate.input | null>
    >((acc, entry) => {
        const date = dates.formatISO(new Date(entry.endDate), {
            representation: "date",
        });

        if (acc[date] == null) {
            acc[date] = {
                start_date: entry.startDate,
                end_date: entry.endDate,
                value: null,
                is_manual: false,
                timezone: dates.deviceTimezone(),
                apple_health_uuids: [],
                human: null,
            };
        }
        acc[date].value = entry.value;
        acc[date].end_date = entry.endDate;
        acc[date].apple_health_uuids.push(entry.uuid);
        return acc;
    }, {});

    type Metrics = {
        [date: string]: {
            SLEEP: server.journal.day.MetricAggregate.input | null;
            STEPS: server.journal.day.MetricAggregate.input | null;
            WEIGHT: server.journal.day.MetricAggregate.input | null;
            BASE_CALORIES: server.journal.day.MetricAggregate.input | null;
            ACTIVE_CALORIES: server.journal.day.MetricAggregate.input | null;
        };
    };

    for (const [date, entry] of Object.entries(weight)) {
        if (!(date in metrics)) {
            metrics[date] = {
                SLEEP: null,
                STEPS: null,
                WEIGHT: null,
                BASE_CALORIES: null,
                ACTIVE_CALORIES: null,
            };
        }

        if (entry != null && entry.value != 0) {
            metrics[date].WEIGHT = entry;
        }
    }

    for (const aggregate of basalAggregates.results) {
        if (!(aggregate.date in metrics)) {
            metrics[aggregate.date] = {
                SLEEP: null,
                STEPS: null,
                WEIGHT: null,
                BASE_CALORIES: null,
                ACTIVE_CALORIES: null,
            };
        }

        metrics[aggregate.date].BASE_CALORIES = {
            start_date: aggregate.startDate,
            end_date: aggregate.endDate,
            value: aggregate.value,
            is_manual: false,
            timezone: dates.deviceTimezone(),
            apple_health_uuids: [],
            human: new Intl.NumberFormat("en-US").format(aggregate.value),
        };
    }

    for (const aggregate of activeAggregates.results) {
        if (!(aggregate.date in metrics)) {
            metrics[aggregate.date] = {
                SLEEP: null,
                STEPS: null,
                WEIGHT: null,
                BASE_CALORIES: null,
                ACTIVE_CALORIES: null,
            };
        }

        metrics[aggregate.date].ACTIVE_CALORIES = {
            start_date: aggregate.startDate,
            end_date: aggregate.endDate,
            value: aggregate.value,
            is_manual: false,
            timezone: dates.deviceTimezone(),
            apple_health_uuids: [],
            human: new Intl.NumberFormat("en-US").format(aggregate.value),
        };
    }

    for (const aggregate of stepsAggregates.results) {
        if (!(aggregate.date in metrics)) {
            metrics[aggregate.date] = {
                SLEEP: null,
                STEPS: null,
                WEIGHT: null,
                BASE_CALORIES: null,
                ACTIVE_CALORIES: null,
            };
        }

        metrics[aggregate.date].STEPS = {
            start_date: aggregate.startDate,
            end_date: aggregate.endDate,
            value: aggregate.value,
            is_manual: false,
            timezone: dates.deviceTimezone(),
            apple_health_uuids: [],
            human: new Intl.NumberFormat("en-US").format(aggregate.value),
        };
    }

    for (const entry of sleep) {
        if (!(entry.date in metrics)) {
            metrics[entry.date] = {
                SLEEP: null,
                STEPS: null,
                WEIGHT: null,
                BASE_CALORIES: null,
                ACTIVE_CALORIES: null,
            };
        }

        if (entry.total_duration != 0) {
            metrics[entry.date].SLEEP = {
                start_date: entry.start_time,
                end_date: entry.end_time,
                value: entry.total_duration,
                is_manual: false,
                timezone: dates.deviceTimezone(),
                apple_health_uuids: entry.apple_health_uuids,
                human: entry.human,
            };
        }
    }

    (store.dispatch as ReturnType<typeof useAppDispatch>)({
        type: "SET_HEALTHKIT_METRICS",
        metrics,
    });
    (store.dispatch as ReturnType<typeof useAppDispatch>)({
        type: "SET_HEALTHKIT_WORKOUTS",
        workouts,
    });

    const metricsPayload = [
        ...prepareMetric("SLEEP", samples.SLEEP.resultData),
        ...prepareMetric("WEIGHT", samples.WEIGHT.resultData),
        ...prepareMetric("WORKOUT", samples.WORKOUT?.resultData ?? []),
    ];

    if (profile.user.casted_dismissed_announcements.includes("DEBUG_HEALTHKIT")) {
        const {deviceUUID} = await hardware.getAppInfo();
        void server.core.rpc.apple_ping({
            device_id: deviceUUID,
            type: "DEBUG_HEALTHKIT_PLUGIN_SAMPLES",
            data: samples,
        });
        void server.core.rpc.apple_ping({
            device_id: deviceUUID,
            type: "DEBUG_HEALTHKIT_METRICS_PAYLOAD",
            data: JSON.stringify(metricsPayload),
        });
    }

    // Skip the round-trip for windows with no samples. Matters for the full
    // resync, which strides 15-day windows back to the earliest sample: a sparse
    // history (long empty stretches) would otherwise fire an empty POST per window.
    if (metricsPayload.length > 0) {
        void server.journal.day.update_metrics(metricsPayload);
    }

    // Check if metrics is empty (Apple may have removed authorization)
    if (Object.keys(metrics).length === 0) {
        return false;
    }

    void server.journal.day.update_metric_aggregates({
        user_id: profile.user.id,
        metrics,
        type: `HEALTHKIT-${trace}`,
        workouts,
        ...(hourlySteps != null ? {hourly_steps: hourlySteps} : {}),
    });

    return true;
}

export async function checkAndSyncHealthData({
    profile,
    trace,
}: {
    profile: server.core.rpc.Profile.output;
    trace: string;
}) {
    const {platform, deviceUUID: uuid} = await hardware.getAppInfo();
    const status = profile.settings.by_device?.[uuid]?.apple_health?.status;

    // SHOULD_REQUEST still syncs: it means "enabled, and the UI may offer an
    // update", never "stop". Devices parked on it heal here on their next open.
    if (platform == "ios" && (status == "ENABLED" || status == "SHOULD_REQUEST")) {
        const didLosePermissions = (await checkPermissions()).some(
            (status) => status.status === "notDetermined",
        );
        const result = await syncHealthData(profile, 7, trace);

        // "shouldRequest" answers for the whole HEALTHKIT_PERMISSIONS set, so it
        // stays true forever for anyone who granted a narrower ask (the walking
        // club requests stepCount alone). It is fuel for the journal card's
        // Update button and nothing else — this path runs with no UI and no user,
        // so it must never write the answer into the device's persisted setting.
        const authStatus = await hardware.HealthPlugin.getRequestStatusForAuthorization(
            hardware.HEALTHKIT_PERMISSIONS,
        ).catch(() => undefined);

        // A never-presented type also reads notDetermined, so while iOS reports
        // there is something new to offer, an empty sync is not proof of
        // revocation — keep the linkage and let the Update button re-present.
        if (
            result == false &&
            didLosePermissions == true &&
            authStatus?.status !== "shouldRequest"
        ) {
            const updatedProfile = produce(profile, (draft) => {
                if (draft.settings.by_device?.[uuid]?.apple_health) {
                    delete draft.settings.by_device[uuid].apple_health;
                }
            });

            (store.dispatch as ReturnType<typeof useAppDispatch>)({
                type: "SET_PROFILE",
                profile: updatedProfile,
            });

            void analytics.track("LOST_APPLE_HEALTH_PERMISSIONS");
            void server.core.rpc.update_settings(updatedProfile.settings);
            return; // Don't attempt sync if re-authorization is needed
        }

        (store.dispatch as ReturnType<typeof useAppDispatch>)({
            type: "SET_HEALTH_NEEDS_UPDATE",
            value: authStatus?.status === "shouldRequest",
        });
    }
}

// The day's step count from Apple Health, for the workout bar: iOS only, with
// Apple Health linked, once a sync has brought steps for the day (this
// session's sync, or one the journal already holds). A sync runs when the
// journal loads; this adds one on every return to the app.
export function useSyncedSteps(journal: server.journal.day.Journal.output) {
    const {deviceUUID, platform} = React.use(hardware.getAppInfo());
    const status = useAppSelector(
        (state) =>
            state.orm.profile?.settings.by_device?.[deviceUUID]?.apple_health?.status,
    );
    const synced = useAppSelector(
        (state) => state.orm.metrics[journal.day.key]?.STEPS?.value ?? null,
    );
    const stored = journal.metrics.STEPS;
    const steps = synced ?? (stored?.is_manual === false ? stored.value : null);
    const isLinked =
        platform == "ios" && (status == "ENABLED" || status == "SHOULD_REQUEST");

    React.useEffect(() => {
        if (!isLinked) return;
        const listener = hardware.App.addListener("appStateChange", (appState) => {
            const profile = store.getState().orm.profile;
            if (appState.isActive && profile != null) {
                void checkAndSyncHealthData({profile, trace: "workout-resume"});
            }
        });
        return () => {
            void listener.then((handle) => handle.remove());
        };
    }, [isLinked]);

    return isLinked ? steps : null;
}

// We sweep in small windows because every sync is idempotent (upsert by HealthKit
// UUID), so no single request approaches the server's ~32MB body limit even for
// users with years of dense data (e.g. Garmin auto-logged workouts).
const RESYNC_WINDOW_DAYS = 15;

// Re-sync the user's entire Apple Health history, 15 days at a time. Idempotent and
// safe to re-run; an interruption simply resumes from the start next time without
// duplicating rows. earliestDate (the user's first sample, ISO) is required and
// bounds the sweep — the caller already resolved it to decide whether to offer the
// re-sync at all, so there's no fallback here. Assumes the caller knows the user is
// on iOS with Apple Health enabled. Marking the resync done (the
// RESYNCED_APPLE_HEALTH flag) is the caller's responsibility.
export async function resyncAllHealthData(
    profile: server.core.rpc.Profile.output,
    earliestDate: string,
) {
    const totalDays =
        dates.differenceInDays(new Date(), dates.parseISO(earliestDate)) +
        RESYNC_WINDOW_DAYS;

    for (let offset = 0; offset < totalDays; offset += RESYNC_WINDOW_DAYS) {
        await syncHealthData(profile, RESYNC_WINDOW_DAYS, "resync", offset);
    }
}

export function AppleHealthDialog(props: {close: (result: unknown) => void}) {
    const dispatch = useAppDispatch();
    const profile = useAppSelector(orm.getProfile)!;

    return (
        <div className="p-4">
            <div>
                <div className="mt-3 text-center sm:mt-5">
                    <h3 className="text-base/6 font-semibold text-gray-900">
                        Enable Apple Health
                    </h3>
                    <div className="mt-2 space-y-4">
                        <p className="text-sm text-gray-500">
                            You will be prompted to authorize Joy to access your Apple
                            Health data.
                        </p>
                        <p className="text-sm text-gray-500">
                            We can only do this once.
                        </p>
                        <p className="text-sm text-gray-500">
                            The dialog can take a few seconds to appear. Be patient.
                        </p>
                        <p className="text-sm text-gray-500">
                            Be sure to click{" "}
                            <strong className="text-gray-900">Turn On All</strong>
                        </p>
                    </div>
                </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <typography.AsyncButton
                    type="button"
                    onClick={async () => {
                        await requestAuthorization();
                        const {deviceUUID: uuid} = await hardware.getAppInfo();

                        const updatedProfile = produce(profile, (draft) => {
                            draft.settings.by_device = draft.settings.by_device ?? {};
                            draft.settings.by_device[uuid] = {
                                ...draft.settings.by_device[uuid],
                                apple_health: {
                                    status: "ENABLED",
                                },
                            };
                        });
                        dispatch({type: "SET_PROFILE", profile: updatedProfile});
                        dispatch({type: "SET_HEALTH_NEEDS_UPDATE", value: false});
                        void server.core.rpc.update_settings(updatedProfile.settings);

                        void syncHealthData(updatedProfile, 30, "initial");
                        dispatch(
                            notify(
                                "Apple Health integration enabled.\n\nYour data will sync shortly.",
                                {type: "success", permanent: true},
                            ),
                        );
                        props.close(null);
                    }}
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid sm:col-start-2"
                >
                    Enable
                </typography.AsyncButton>
                <button
                    type="button"
                    data-autofocus
                    onClick={() => {
                        void analytics.track("CANCELED_APPLE_HEALTH_AUTHORIZATION");
                        props.close(null);
                    }}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}

export function AppleHealth() {
    const enableAppleHealth = dialogs.useDialog({
        Content: ({close}) => {
            return <AppleHealthDialog close={close} />;
        },
    });

    return (
        <>
            {enableAppleHealth.dialog}
            <typography.Button expand onClick={() => enableAppleHealth()}>
                Sync with Apple Health
            </typography.Button>
        </>
    );
}
